import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card, Image, Row, Col, Button, Form } from "react-bootstrap";
import { FaSchool } from "react-icons/fa";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface IDCardProps {
  type: "student" | "teacher" | "school";
  variant: "style1" | "style2" | "style3";
  color: string;
  data: {
    data: any;
    school_name: string;
    school_logo?: string;
    teacher_id?: string;
    teacher_name?: string;
    student_id?: string;
    student_name?: string;
    photo?: string;
    sex?: string;
    age?: string | null;
    address?: string;
    date_of_birth?: string | null;
    marital_status?: string;
    state_of_origin?: string;
    mobile_number?: string;
    email_address?: string;
    qualification?: string;
    working_experience?: string;
    religion?: string;
    last_place_of_work?: string;
    account_name?: string;
    account_number?: string;
    bank?: string;
    school_location?: string;
    section?: string;
    subject?: string;
    class_name?: string;
    derived_role?: string;
  };
  showDownloadButton?: boolean;
  variantSelect?: boolean;
}

const IDCard: React.FC<IDCardProps> = ({ type, variant, color, data, showDownloadButton, variantSelect }) => {
  const [selectedVariant, setSelectedVariant] = useState(variant);

  const defaultUserImage = "https://via.placeholder.com/150";
  const defaultSchoolLogo = "https://via.placeholder.com/100";

  const cardStyle = {
    border: `2px solid ${color}`,
    position: "relative" as const,
    overflow: "hidden",
  };

  const watermarkStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: 0.1,
    zIndex: 0,
    width: "70%",
    height: "auto",
  };

  const brandingStyle = {
    position: "absolute" as const,
    bottom: "5px",
    right: "5px",
    zIndex: 10,
    color: "#888",
    fontSize: "0.8rem",
    textAlign: "right" as const,
  };

  const renderFront = () => (
    <div>
      <Card.Header className="text-center" style={{ backgroundColor: color, color: "white" }}>
        <FaSchool className="me-2" />
        <h5>{data?.data?.school_name}</h5>
      </Card.Header>
      <Card.Body className="text-center">
        <Image
          src={data?.data?.photo || defaultUserImage}
          alt={type === "teacher" ? data?.data?.teacher_name : data?.data?.student_name}
          roundedCircle
          style={{ width: "100px", height: "100px", objectFit: "cover", border: `3px solid ${color}` }}
          className="mb-3"
        />
        <Card.Title>
          {type === "teacher" ? data?.data?.teacher_name : type === "student" ? data?.data?.student_name : data?.data?.school_name}
        </Card.Title>
        {type === "teacher" && <p>Subject: {data?.data?.subject || "N/A"}</p>}
        {type === "student" && <p>Class: {data?.data?.class_name || "N/A"}</p>}
      </Card.Body>
    </div>
  );

  const renderBack = () => (
    <Card.Body>
      <h5 className="text-center mb-3">Details</h5>
      <ul className="list-unstyled">
        {data?.data?.age && <li><strong>Age:</strong> {data?.data?.age}</li>}
        {data?.data?.address && <li><strong>Address:</strong> {data?.data?.address}</li>}
        {data?.data?.state_of_origin && <li><strong>State of Origin:</strong> {data?.data?.state_of_origin}</li>}
        {data?.data?.mobile_number && <li><strong>Mobile:</strong> {data?.data?.mobile_number}</li>}
        {data?.data?.email_address && <li><strong>Email:</strong> {data?.data?.email_address}</li>}
        {type === "teacher" && data?.data?.qualification && <li><strong>Qualification:</strong> {data?.data?.qualification}</li>}
      </ul>
      <div className="text-center mt-4">
        <QRCodeSVG
          value={JSON.stringify(data)}
          size={100}
          level="H"
        />
      </div>
    </Card.Body>
  );

  const renderCard = (variantToRender: string) => {
    switch (variantToRender) {
      case "style1":
        return (
          <Card style={cardStyle} className="mb-4" id="id-card">
            <Image
              src={data?.data?.school_logo || defaultSchoolLogo}
              alt="School Logo"
              style={watermarkStyle}
            />
            {renderFront()}
            {renderBack()}
            <div style={brandingStyle}>
              <strong>Generated by EliteEdu Tech</strong>
            </div>
          </Card>
        );
      case "style2":
        return (
          <Card style={cardStyle} className="mb-4" id="id-card">
            <Image
              src={data?.data?.school_logo || defaultSchoolLogo}
              alt="School Logo"
              style={watermarkStyle}
            />
            <Row>
              <Col md={4} className="d-flex justify-content-center align-items-center">
                <Image
                  src={data?.data?.photo || defaultUserImage}
                  alt={type === "teacher" ? data?.data?.teacher_name : data?.data?.student_name}
                  roundedCircle
                  style={{ width: "75px", height: "75px", objectFit: "cover" }}
                />
              </Col>
              <Col md={8}>{renderBack()}</Col>
            </Row>
            <div style={brandingStyle}>
              <strong>Generated by EliteEdu Tech</strong>
            </div>
          </Card>
        );
      case "style3":
        return (
          <Card style={cardStyle} className="mb-4 text-center" id="id-card">
            <Image
              src={data?.data?.school_logo || defaultSchoolLogo}
              alt="School Logo"
              style={watermarkStyle}
            />
            {renderFront()}
            <Card.Footer>
              <QRCodeSVG
                value={JSON.stringify(data)}
                size={75}
                level="H"
              />
            </Card.Footer>
            <div style={brandingStyle}>
              <strong>Generated by EliteEdu Tech</strong>
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
    const card = document.getElementById("id-card");
    if (card) {
      const canvas = await html2canvas(card);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      pdf.addImage(imgData, "PNG", 10, 10, 190, 0);
      pdf.save("id-card.pdf");
    }
  };

  return (
    <div>
      {variantSelect && (
        <Form.Group className="mb-3">
          <Form.Label>Select Variant</Form.Label>
          <Form.Select value={selectedVariant} onChange={(e) => setSelectedVariant(e.target.value as "style1" | "style2" | "style3")}>
            <option value="style1">Style 1</option>
            <option value="style2">Style 2</option>
            <option value="style3">Style 3</option>
          </Form.Select>
        </Form.Group>
      )}

      {renderCard(selectedVariant)}

      {showDownloadButton && (
        <Button variant="primary" onClick={handleDownloadPDF} className="mt-3">
          Download as PDF
        </Button>
      )}
    </div>
  );
};

export default IDCard;
